import { setSelectors, addListeners } from './utilities.js';
import { updateCart, removeItemFromCart, getCart } from './cart-service.js';

class RcQuickCart extends HTMLElement {

  constructor() {
    super();
    this.autoOpen = false;
    this.selectors = {};
    this.events = {};
    this._isFetching = false;
    this._actionsVisibilityObserver = null;
  };

  connectedCallback() { this.initialize(); };

  disconnectedCallback() { this.deinitialize(); };

  _withLoading = async (fn) => {
    this.setAttribute('data-loading', 'true');
    try {
      return await fn();
    } finally {
      this.setAttribute('data-loading', 'false');
    };
  };

  _handleCartUpdate = (event) => {
    if(!event || !event.detail) return;
    const { id, key, value } = event.detail;
    const targetId = key || id;
    if(!targetId || typeof value !== 'number') return;
    this._withLoading(async () => {
      await updateCart(targetId, value);
      await this._refreshItems();
    });
  };

  _handleRemoveItem = async (event) => {
    if(!event || !event.target) return;
    const btn = event.target.closest('[data-item-remove]');
    if(!btn || !this.selectors.cartItems || !this.selectors.cartItems.contains(btn)) return;
    event.preventDefault();
    const cartKey = btn.getAttribute('data-item-key');
    if(!cartKey) return;
    await this._withLoading(async () => {
      const result = await removeItemFromCart(cartKey);
      if(result) await this._refreshItems();
    });
  };

  _open = ({refresh = false} = {}) => {
    this.setAttribute('open', '');
    document.body.setAttribute('data-overlay-active', 'true');
    if(refresh) this._withLoading(() => this._refreshItems());
  };

  _close = () => {
    this.removeAttribute('open');
    document.body.removeAttribute('data-overlay-active');
  };

  _refreshItems = async () => {
    if(this._isFetching || !this.selectors.cartItems) return;
    this._isFetching = true;
    try {
      const html = await getCart('quick-cart-items');
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const updated = tmp.querySelector('.qc-items-surface');
      if(updated && this.selectors.cartItems) this.selectors.cartItems.innerHTML = updated.innerHTML;
    } finally {
      this._isFetching = false;
    };
  };

  _onCartChange = () => { if(this.selectors.cartItems) this._withLoading(() => this._refreshItems()); };

  _setFixedActionsVisible = (isVisible) => {
    if(!this.selectors.fixedActions) return;
    this.selectors.fixedActions.classList.toggle('opacity-0', !isVisible);
    this.selectors.fixedActions.classList.toggle('pointer-events-none', !isVisible);
  };

  _setupActionsVisibilityObserver = () => {
    if(this._actionsVisibilityObserver) {
      this._actionsVisibilityObserver.disconnect();
      this._actionsVisibilityObserver = null;
    }
    if(!this.selectors.scrollSurface || !this.selectors.fixedActions || !this.selectors.originalActions) {
      this._setFixedActionsVisible(true);
      return;
    }
    this._actionsVisibilityObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      this._setFixedActionsVisible(!entry?.isIntersecting);
    }, {
      root: this.selectors.scrollSurface,
      threshold: 0.15
    });
    this._actionsVisibilityObserver.observe(this.selectors.originalActions);
  };

  initialize = () => {
    this.autoOpen = this.getAttribute('data-auto-open') === 'true';
    this.events = window.umbrellaConfig.events;
    addListeners(this, {
      [this.events.quantityIncrement]: this._handleCartUpdate,
      [this.events.quantityDecrement]: this._handleCartUpdate
    });
    this.selectors = setSelectors(this, {
      overlay: '[data-overlay]',
      quickCartClose: '[data-close]',
      cartItems: 'cart-items',
      scrollSurface: '.min-h-0.flex-1.overflow-y-auto',
      fixedActions: '[data-qc-fixed-actions]',
      originalActions: '[data-qc-original-actions]'
    });
    if(this.selectors.overlay) this.selectors.overlay.addEventListener('click', () => this._close());
    if(this.selectors.cartItems) this.selectors.cartItems.addEventListener('click', this._handleRemoveItem);
    if(this.selectors.quickCartClose) this.selectors.quickCartClose.addEventListener('click', () => this._close());
    if(this.events.qcOpen) document.addEventListener(this.events.qcOpen, this._open);
    if(this.events.cartChange) document.addEventListener(this.events.cartChange, this._onCartChange);
    if(this.events.cartAdded && this.autoOpen) document.addEventListener(this.events.cartAdded, () => this._open({refresh: true}));
    this._setupActionsVisibilityObserver();
  };

  deinitialize = () => {
    // Ensure global overlay state doesn't get stuck if this element is removed while open.
    this._close();
    if (this.selectors.overlay) this.selectors.overlay.removeEventListener('click', () => this._close());
    if(this.selectors.quickCartClose) this.selectors.quickCartClose.removeEventListener('click', () => this._close());
    if(this.selectors.cartItems) this.selectors.cartItems.removeEventListener('click', this._handleRemoveItem);
    if(this.events && this.events.qcOpen) document.removeEventListener(this.events.qcOpen, this._open);
    if(this.events && this.events.cartChange) document.removeEventListener(this.events.cartChange, this._onCartChange);
    if(this.events && this.events.cartAdded) document.removeEventListener(this.events.cartAdded, () => this._open());
    if(this._actionsVisibilityObserver) this._actionsVisibilityObserver.disconnect();
  };
};

if(!customElements.get('rc-quick-cart')) customElements.define('rc-quick-cart', RcQuickCart);